"use client";

import TextInputField from "@components/ui/form/TextInputField/TextInputField";
import { CONTACT_EMAIL_TEST_ID } from "@e2e/helpers/checkout/components/contact-details/constants";
import type { ContactDetailsInitialValues } from "@lib/utils/checkoutHelper";
import { fieldStateHelper } from "@lib/utils/form/fieldStateHelper";
import { FormControl } from "@ecco/ui/form-control";
import { FormErrorMessage } from "@ecco/ui/form-error";
import { getSuggestedEmailCorrection } from "@lib/utils/emailDomainSuggestion";
import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Checkout, CommonCMS } from "@lib/constants/contentful";
import { space, colors, typography } from "@ecco/ui/tokens";

export default function EmailField() {
    const { get } = useMicrocopy();

    const { register, formState, control } = useFormContext<ContactDetailsInitialValues>();
    const { errors, touchedFields } = formState;

    const email = useWatch({ control: control, name: "email", defaultValue: "" });
    const emailSuggestion = useMemo(() => getSuggestedEmailCorrection(email ?? ""), [email]);
    const [ignoredEmail, setIgnoredEmail] = useState<string | null>(null);

    const { getFieldStateProps, getIconProps } = fieldStateHelper({
        errors,
        touched: touchedFields,
        type: "hook-form",
        isSubmitted: formState.isSubmitted,
    });

    const showEmailSuggestion =
        !!emailSuggestion &&
        ignoredEmail !== email &&
        (touchedFields.email || formState.isSubmitted) &&
        !errors.email;

    useEffect(() => {
        setIgnoredEmail(null);
    }, [email]);

    const { isInvalid: isEmailInvalid, ...restEmailFieldStateProps } = getFieldStateProps("email");

    return (
        <>
            <TextInputField
                {...register("email")}
                {...restEmailFieldStateProps}
                {...getIconProps("email")}
                {...(showEmailSuggestion
                    ? {
                          isInvalid: true,
                          isSuccess: false,
                          rightIcon: undefined,
                      }
                    : {})}
                header={get(CommonCMS.page, Checkout.step1.formEmail.title)}
                testId={CONTACT_EMAIL_TEST_ID}
                isInvalid={showEmailSuggestion || isEmailInvalid}
                aria-invalid={showEmailSuggestion || isEmailInvalid}
                name="email"
                type="email"
            />
            {showEmailSuggestion && (
                <FormControl isInvalid={true} isSuccess={false} style={{ marginTop: -10 }}>
                    <FormErrorMessage>
                        {get(CommonCMS.page, Checkout.step1.formEmail.suggestion, {
                            replacements: { emailSuggestion },
                        })}
                        <button
                            style={{
                                marginLeft: space["1"],
                                color: colors.black,
                                fontWeight: typography.fontWeights.bold,
                            }}
                            onClick={() => setIgnoredEmail(email)}
                        >
                            {get(CommonCMS.page, Checkout.step1.formEmail.originIsCorrect)}
                        </button>
                    </FormErrorMessage>
                </FormControl>
            )}
        </>
    );
}
